import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { Link, useHistory } from "react-router-dom";
import {
  average,
  max,
  min,
  quantile,
  standardDeviation,
} from "simple-statistics";
import { ReactComponent as AddCircle } from "../../../../assets/img/icon/add_circle_black_24dp.svg";
import { ReactComponent as ChevronRight } from "../../../../assets/img/icon/chevron_right_black_24dp.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/img/icon/close_black_24dp.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/img/icon/delete_black_24dp.svg";
import { ReactComponent as DoneIcon } from "../../../../assets/img/icon/done_black_24dp.svg";
import { ReactComponent as People } from "../../../../assets/img/icon/people_black_24dp.svg";
import { ReactComponent as PrintIcon } from "../../../../assets/img/icon/print_24dp.svg";
import { COLOR, GRAPH_COLOR, VARIABLE } from "../../../../config/constant";
import {
  formatter,
  getDateFormat2,
  getPercPrediction,
} from "../../../../helpers/helper";
import { Patient } from "../../../../models/Patient";
import { getPulmonaryFunc } from "../../../../services/httpService/pulmonaryFuncRequest";
import {
  deleteRecord,
  getRecordDetail,
  patchRecord,
} from "../../../../services/httpService/recordRequest";
import { GlobalContext } from "../../../contexts/globalContext";
import Button from "../../../ui/atoms/Button/Button";
import ChartLine from "../../../ui/atoms/ChartLine/ChartLine";
import PageTitle from "../../../ui/atoms/PageTitle/PageTitle";
import PatientInfo from "../../../ui/atoms/PatientInfo/PatientInfo";
import RecordLoader from "../../../ui/molecules/RecordLoader/RecordLoader";

const statistics = [
  { label: "Count" },
  { label: "Average", cb: average },
  { label: "Standard Deviation", cb: standardDeviation },
  { label: "Minimum", cb: min },
  { label: "25%", cb: (data) => quantile(data, 0.25) },
  { label: "50%", cb: (data) => quantile(data, 0.5) },
  { label: "75%", cb: (data) => quantile(data, 0.75) },
  { label: "Maximum", cb: max },
];

const parameters = [
  { label: "FVC", unit: "L", key: "FVC" },
  { label: "FEV1", unit: "L", key: "FEV1" },
  { label: "FEV1/FVC", unit: "%", key: "ratio" },
  { label: "PEFR", unit: "L/s", key: "PEF" },
];

const PatientReport = () => {
  const isMounted = useRef(true);
  const params = useParams();
  const history = useHistory();
  const [sharedState, dispatch] = useContext(GlobalContext);
  const [patient, setPatient] = useState();
  const [result, setResult] = useState({});
  const [record, setRecord] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState(false);
  const [pulmonaryFunc, setPulmonaryFunc] = useState({
    FEV: "",
    FEV1: "",
    ratio: "",
    PEF: "",
  });

  const handleGetData = async () => {
    try {
      let responses = await Promise.all([
        getRecordDetail(params.recordId),
        getPulmonaryFunc(params.id),
      ]);

      let recordRes = responses[0].result;
      let pulmonaryFunc = responses[1].result;
      setPulmonaryFunc(pulmonaryFunc);

      let record = {
        createdAt: getDateFormat2(recordRes.createdAt, true),
        notes: recordRes.notes,
        testNumber: recordRes.testNumber,
        device: recordRes.device,
        _id: recordRes._id,
        mode: recordRes.mode,
      };

      if (record.mode === "basic") {
        record["sensors"] = {
          data: recordRes.sensors.RR.map((el, idx) => ({
            idx: idx * 2,
            RR: recordRes.sensors.RR[idx],
            T: recordRes.sensors.T[idx],
            SPO2: recordRes.sensors.SPO2[idx],
            HR: recordRes.sensors.HR[idx],
          })),
        };

        calculateStatistic({
          RR: recordRes.sensors.RR,
          T: recordRes.sensors.T,
          SPO2: recordRes.sensors.SPO2,
          HR: recordRes.sensors.HR,
        });
      } else if (record.mode === "spirometer") {
        record["sensors"] = {
          data1: recordRes.sensors.FC.map((el, idx) => ({
            FC: el,
            VC: recordRes.sensors.VC[idx],
          })),
          data2: recordRes.sensors.VT.map((el, idx) => ({
            VT: el,
            idx: idx / 50,
          })),
          FEV1: recordRes.sensors.FEV1,
          FVC: recordRes.sensors.FVC,
          PEF: recordRes.sensors.PEF,
          ratio: recordRes.sensors.ratio * 100,
        };

        let pcPred = {};
        parameters.forEach((param) => {
          recordRes.sensors[param.key] =
            param.key === "ratio"
              ? recordRes.sensors[param.key] * 100
              : recordRes.sensors[param.key];
          pcPred[param.key] = getPercPrediction(
            pulmonaryFunc[param.key],
            recordRes.sensors[param.key]
          );
        });
        record["pcPred"] = pcPred;
      }

      if (!isMounted.current) return null;

      setRecord(record);
      setPatient(
        new Patient(
          recordRes.patient._id,
          recordRes.patient.user.name,
          recordRes.patient.user.profile
        )
      );

      let defaultNote = [];
      for (let idx = 0; idx < 5; idx++) {
        defaultNote[idx] = {
          show: record.notes[idx] ? true : false,
          text: record.notes[idx] ? record.notes[idx] : "",
        };
      }
      if (record.notes.length === 0) defaultNote[0].show = true;
      if (isMounted.current) setNotes(defaultNote);
    } catch (error) {
      dispatch("alert", {
        message: `ID Rekam medis : "${params.recordId}" tidak ditemukan`,
        show: true,
        type: "warning",
      });
      history.push(`/patient/${params.id}`);
    }
  };

  const calculateStatistic = (inputData) => {
    const keys = Object.keys(inputData);
    let data = {};
    keys.forEach((key) => {
      data[key] = {};
      data[key]["data"] = inputData[key];
    });

    keys.forEach((key) => {
      statistics.forEach((stat) => {
        if (stat.cb && data[key]["data"].length !== 0)
          data[key][stat.label] = formatter.format(stat.cb(data[key]["data"]));
      });
    });

    keys.forEach((key) => {
      data[key]["Count"] = data[key]["data"].length;
    });
    if (isMounted.current) setResult(data);
  };

  const handleSaveNote = async () => {
    try {
      let updatedNotes = [];
      notes.forEach((note) => {
        if (note.show === true && note.text !== "")
          updatedNotes = [...updatedNotes, note.text];
      });

      if (updatedNotes.length === 0)
        throw new Error("Silakan isi review terlebih dahulu.");

      dispatch("isLoading", true);

      let { result } = await patchRecord({
        _id: params.recordId,
        notes: updatedNotes,
      });

      await setRecord((prevState) => ({
        ...prevState,
        notes: result.notes,
      }));

      let defaultNote = [];
      for (let idx = 0; idx < 5; idx++) {
        defaultNote[idx] = {
          show: result.notes[idx] ? true : false,
          text: result.notes[idx] ? result.notes[idx] : "",
        };
      }
      if (result.notes.length === 0) defaultNote[0].show = true;
      if (!isMounted.current) return null;
      setNotes(defaultNote);
      setForm(false);

      dispatch("isLoading", false);
    } catch (error) {
      if (!isMounted.current) return null;
      dispatch("isLoading", false);
      dispatch("alert", {
        show: true,
        type: "warning",
        message: error.message,
      });
    }
  };

  const handleRemoveNote = async (idx) => {
    try {
      let updatedNotes = [...notes];
      let trueNotes = notes.filter((note) => note.show === true);
      if (trueNotes.length === 1)
        throw new Error("Tidak bisa menghapus review");

      updatedNotes[idx].show = false;
      updatedNotes[idx].text = "";
      if (isMounted.current) setNotes(updatedNotes);
    } catch (error) {
      if (isMounted.current)
        dispatch("alert", {
          show: true,
          type: "warning",
          message: error.message,
        });
    }
  };

  const handleAddNotes = async () => {
    try {
      let updatedNotes = [...notes];
      const idx = notes.findIndex((note) => note.show === false);
      if (idx < 0) throw new Error("review melebihi batas maksimum.");
      updatedNotes[idx].show = true;
      if (isMounted.current) setNotes(updatedNotes);
    } catch (error) {
      if (isMounted.current)
        dispatch("alert", {
          show: true,
          type: "warning",
          message: error.message,
        });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedNotes = [...notes];
    updatedNotes[name].text = value;
    setNotes(updatedNotes);
  };

  const setDefaultNote = () => {
    let defaultNote = [];
    for (let idx = 0; idx < 5; idx++) {
      defaultNote[idx] = {
        show: record.notes[idx] ? true : false,
        text: record.notes[idx] ? record.notes[idx] : "",
      };
    }
    if (record.notes.length === 0) defaultNote[0].show = true;
    // console.log(defaultNote);
    if (isMounted.current) setNotes(defaultNote);
  };

  const handleCloseForm = () => {
    setDefaultNote();
    setForm(false);
  };

  const handleOpenForm = () => {
    setDefaultNote();
    setForm(true);
  };
  const handleOpenConfirm = () => {
    dispatch("confirm", {
      show: true,
      message: `Lanjutkan hapus rekam medis ini?`,
      callback: handleDeleteRecord,
    });
  };

  const handleDeleteRecord = async () => {
    try {
      dispatch("confirm", {
        show: false,
        message: null,
        callback: null,
      });
      dispatch("isLoading", true);
      await deleteRecord({ _id: params.recordId });
      if (!isMounted.current) return null;
      dispatch("isLoading", false);
      history.push(`/patient/${params.id}`);
    } catch (error) {
      if (!isMounted.current) return null;
      dispatch("isLoading", false);
      dispatch("alert", {
        show: true,
        message: error.message,
        type: "warning",
      });
    }
  };

  useEffect(() => {
    handleGetData();
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <>
      <div className="mt-2 lg:mt-6">
        <PageTitle
          title="Halaman Pasien"
          description="Dapatkan informasi pasien beserta dengan riwayat medisnya."
          icon={
            <People className="w-10 sm:w-12 lg:w-16 h-10 sm:h-12 lg:h-16 fill-current text-white" />
          }
        />
        <PatientInfo patient={patient} className="mt-6" />

        <div className="mt-10 flex justify-end items-center">
          <div
            className="text-primary cursor-pointer flex items-center "
            onClick={handleOpenConfirm}
          >
            <div>
              <DeleteIcon fill={COLOR.PRIMARY} className="w-6 h-6" />
            </div>
            <div className="ml-1 border-b border-transparent hover:border-primary">
              Hapus
            </div>
          </div>
          <Link to={`/patient/${params.id}`}>
            <div className="text-primary cursor-pointer flex items-center ">
              <div>
                <ChevronRight fill={COLOR.PRIMARY} className="w-6 h-6" />
              </div>
              <div className="ml-1 border-b border-transparent hover:border-primary">
                Kembali
              </div>
            </div>
          </Link>
        </div>

        <div className="flex flex-col xl:flex-row mt-6 space-x-0 xl:space-x-5 space-y-5 xl:space-y-0 pb-24">
          <div className="w-full xl:w-64 xl:flex-shrink-0">
            <div className="bg-primary rounded-lg shadow-lg px-3 py-2 text-white">
              {record._id === undefined ? (
                <div className="-my-0.5 -mx-0.5">
                  <div className="bg-secondary animate-pulse rounded my-1 h-5 w-2/3" />
                  <div className="bg-secondary animate-pulse rounded my-1 h-5 w-full" />
                  <div className="bg-secondary animate-pulse rounded my-1 h-5 w-full" />
                </div>
              ) : (
                <>
                  <div className="uppercase font-semibold">
                    {`Tes - ${record.testNumber}`}
                  </div>
                  <ul className="list-disc list-inside">
                    <li>{record.createdAt}</li>
                    <li>{record.device}</li>
                  </ul>
                </>
              )}
            </div>
          </div>
          <div className="w-full">
            <div className="border border-primary shadow-lg rounded-lg px-2 md:px-4 pt-3 pb-5">
              {record._id === undefined ? (
                <RecordLoader />
              ) : (
                <>
                  {record.mode === "basic" && (
                    <div>
                      {/* <h4 className="font-semibold">
                        Hasil Pengukuran Mode Basic
                      </h4> */}
                      <div className="grid grid-cols-2 justify-between">
                        <h4 className="font-semibold">
                          Hasil Pengukuran Mode Basic
                        </h4>
                        <div className="w-100 flex justify-end mx-5" >
                          <PrintIcon onClick={() => { document.getElementById("print-basic").click() }} className="mx-2 cursor-pointer" fill="#1F9096" />
                          <a id="print-basic" href={"/print/" + params.recordId} className="text-primary cursor-pointer hover:underline"> Print</a>
                        </div>

                      </div>
                      <div className="mt-5">
                        &bull; Grafik Pengukuran RR, T, SPO2 dan HR.
                      </div>
                      <div className="grid grid-cols-12 gap-3 mt-2">
                        {VARIABLE.basic.map((variable, idx) => (
                          <div
                            key={"line-chart" + variable.key}
                            className={
                              "w-full h-56 rounded-md text-xs shadow px-2 pt-3 pb-5 " +
                              (expanded === true
                                ? "col-span-12"
                                : "col-span-12 lg:col-span-6")
                            }
                            onClick={() => {
                              setExpanded(!expanded);
                            }}
                          >
                            <div className="text-center -mb-1">
                              {`Grafik ${idx + 1}. Grafik ${variable.key
                                } terhadap Waktu`}
                            </div>
                            <ChartLine
                              stroke={[GRAPH_COLOR[idx]]}
                              data={record.sensors.data}
                              dataKeyX="idx"
                              xAxisLabel="Waktu (s)"
                              dataKeyY={[variable.key]}
                              yAxisLabel={variable.key}
                              type="number"
                            />
                          </div>
                        ))}
                      </div>

                      <div className="mt-5">&bull; Data Statistik</div>
                      <div className="mt-2 w-full overflow-x-auto px-0.5">
                        <table className="w-full mt-2 text-sm">
                          <thead>
                            <tr>
                              <th className="bg-primary border border-white text-white px-1 py-3 w-56">
                                Item
                              </th>
                              {VARIABLE["basic"].map((obj) => (
                                <th
                                  key={"thead" + obj.key}
                                  className="bg-primary border border-white text-white px-1 py-3"
                                >
                                  {obj.key}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {statistics.map((statistic, idx) => (
                              <tr
                                key={"row-stat" + idx}
                                className={
                                  idx % 2 === 0
                                    ? "bg-primary bg-opacity-20"
                                    : ""
                                }
                              >
                                <td className="px-1 py-1.5">
                                  {statistic.label}
                                </td>
                                {VARIABLE["basic"].map((obj) => {
                                  return (
                                    <td
                                      key={statistic.label + obj.key}
                                      className="px-1 py-1.5 text-center"
                                    >
                                      {`${result[obj.key] &&
                                          result[obj.key][statistic.label]
                                          ? result[obj.key][statistic.label]
                                          : "-"
                                        }`}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {record.mode === "spirometer" && (
                    <div>
                      {/* <h4 className="font-semibold">
                        Hasil Pengukuran Mode Spirometer
                      </h4> */}
                      <div className="grid grid-cols-2 justify-between">
                        <h4 className="font-semibold">
                          Hasil Pengukuran Mode Spirometer
                        </h4>
                        <div className="w-100 flex justify-end mx-5" >
                          <PrintIcon onClick={() => { document.getElementById("print-spiro").click() }} className="mx-2 cursor-pointer" fill="#1F9096" />
                          <a id="print-spiro" href={"/print/" + params.recordId} className="text-primary cursor-pointer hover:underline"> Print</a>
                        </div>

                      </div>
                      <div className="mt-2">&bull; Data Pengukuran.</div>

                      <table className="mt-2 w-full">
                        <thead>
                          <tr className="bg-secondary text-white">
                            <th className="px-1 py-1 w-48">Parameter</th>
                            <th className="px-1 py-1 w-20">Satuan</th>
                            <th className="px-1 py-1 w-20">Prediksi</th>
                            <th className="px-1 py-1 w-20">Pengukuran</th>
                            <th className="px-1 py-1 w-20">% Prediksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parameters.map((parameter, idx) => (
                            <tr
                              key={"row" + parameter.key}
                              className={
                                "bg-secondary " +
                                (idx % 2 === 0
                                  ? "bg-opacity-0"
                                  : "bg-opacity-20")
                              }
                            >
                              <td className="px-1 py-1.5">{parameter.label}</td>
                              <td className="px-1 py-1.5 text-center">
                                {parameter.unit}
                              </td>
                              <td className="px-1 py-1.5 text-center">
                                {pulmonaryFunc[parameter.key]
                                  ? pulmonaryFunc[parameter.key].toFixed(2)
                                  : "-"}
                              </td>
                              <td className="px-1 py-1.5 text-center">
                                {record.sensors[parameter.key]
                                  ? record.sensors[parameter.key].toFixed(2)
                                  : "-"}
                              </td>
                              <td className="px-1 py-1.5 text-center">
                                {record.pcPred[parameter.key]
                                  ? record.pcPred[parameter.key].toFixed(2)
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div className="mt-5">
                        &bull; Grafik Pengukuran VT, V dan F.
                      </div>
                      <div className="grid grid-cols-12 gap-3 mt-2">
                        <div
                          className={
                            "w-full h-64 rounded-md text-xs shadow px-2 pt-3 pb-5 col-span-12 sm:col-span-6"
                          }
                        >
                          <div className="text-center -mb-1">
                            Grafik 1. Grafik VT terhadap Waktu
                          </div>
                          <ChartLine
                            stroke={[COLOR.INFO]}
                            data={record.sensors.data2}
                            dataKeyX="idx"
                            xAxisLabel="Waktu (s)"
                            dataKeyY={["VT"]}
                            yAxisLabel="VT (L)"
                            type="number"
                            xdomain={[0, 10]}
                            ydomain={[0, 8]}
                            xticks={new Array(10).fill(0).map((x, idx) => idx)}
                          />
                        </div>
                        <div
                          className={
                            "w-full h-64 rounded-md text-xs shadow px-2 pt-3 pb-5 col-span-12 sm:col-span-6"
                          }
                        >
                          <div className="text-center -mb-1">
                            Grafik 2. F terhadap V
                          </div>
                          <ChartLine
                            stroke={[COLOR.SECONDARY]}
                            data={record.sensors.data1}
                            dataKeyX="VC"
                            xAxisLabel="V (L)"
                            dataKeyY={["FC"]}
                            yAxisLabel="F (L/s)"
                            type="number"
                            xdomain={[0, 20]}
                            ydomain={[0, 10]}
                            yticks={new Array(5)
                              .fill(0)
                              .map((x, idx) => idx * 2)}
                            xticks={new Array(10)
                              .fill(0)
                              .map((x, idx) => idx * 2)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-6">
                    <h4 className="font-semibold">Review Dokter</h4>

                    {record.notes.length === 0 && form === false ? (
                      <div>
                        Tidak ada catatan dokter. Klik{" "}
                        <span
                          className="text-primary underline cursor-pointer"
                          onClick={handleOpenForm}
                        >
                          disini
                        </span>{" "}
                        untuk menulis review.
                      </div>
                    ) : (
                      <div className="mt-3">
                        {notes.map((note, idx) => {
                          if (note.show === true)
                            return (
                              <div
                                className="mt-3 flex items-center space-x-2"
                                key={"input-note-" + idx}
                              >
                                {form ? (
                                  <>
                                    <div className="flex-shrink-0">
                                      <div
                                        className="cursor-pointer bg-warning bg-opacity-40 shadow rounded-full p-0.5 hover:bg-opacity-80"
                                        onClick={() => handleRemoveNote(idx)}
                                      >
                                        <CloseIcon
                                          className="w-3 h-3"
                                          fill={"#fafafa"}
                                        />
                                      </div>
                                    </div>
                                    <textarea
                                      className="w-full border rounded-lg px-2 py-1 shadow focus:outline-none bg-white"
                                      rows="3"
                                      name={idx}
                                      onChange={handleChange}
                                      value={note.text}
                                      disabled={!form}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <div className="flex-shrink-0">
                                      <div className="cursor-pointer bg-secondary bg-opacity-90 shadow rounded-full p-0.5">
                                        <DoneIcon
                                          className="w-3 h-3"
                                          fill={"#fafafa"}
                                        />
                                      </div>
                                    </div>
                                    <div className="w-full border rounded-lg px-2 py-1 shadow focus:outline-none bg-white">
                                      {note.text}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                        })}
                      </div>
                    )}

                    {form ? (
                      <div className="mt-5 flex space-x-2 justify-between items-center">
                        <button
                          className="ml-5 px-1.5 py-1 rounded flex items-center space-x-1 opacity-50 hover:opacity-100 focus:ring-none"
                          onClick={handleAddNotes}
                        >
                          <AddCircle className="w-5 h-5" fill={COLOR.PRIMARY} />
                          <div>Tambah Review</div>
                        </button>
                        <div className="flex space-x-2">
                          <div>
                            <Button
                              className="bg-warning px-2 py-1 rounded text-white"
                              handleClick={handleCloseForm}
                            >
                              Batal
                            </Button>
                          </div>
                          <div>
                            <Button
                              className="bg-secondary px-2 py-1 rounded text-white"
                              handleClick={handleSaveNote}
                            >
                              Simpan
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      record.notes.length !== 0 && (
                        <div className="flex justify-end mt-5">
                          <Button
                            className="bg-secondary text-sm px-1.5 py-1 rounded text-white"
                            handleClick={handleOpenForm}
                          >
                            Ubah Review
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PatientReport;
